import React from "react";
import {
  Store,
  Package,
  ShoppingCart,
  TrendingUp,
  Users,
  Link2,
  RefreshCw,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  StatusBadge,
} from "@/components/ui";
import { useAdminDashboard } from "@/hooks/useAdminDashboard";
import { showToast } from "@/components/ui";
import { formatCompactInr } from "@/lib/formatCompactInr";

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtPct(pct: number): string {
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

const syncStatusMap: Record<string, string> = {
  SUCCESS: "ACTIVE",
  RUNNING: "WARNING",
  FAILED: "FAILED",
  QUEUED: "PENDING",
  PARTIAL_SUCCESS: "DEGRADED",
};

export default function Dashboard() {
  const { data, loading, error, refetch } = useAdminDashboard();

  const statCards = [
    {
      label: "Total Sellers",
      value: loading ? "—" : fmt(data?.sellers.totalCount ?? 0),
      change: loading ? null : (data?.sellers.dayOnDayChangePct ?? 0),
      icon: Store,
      color: "indigo",
      sub: loading ? "" : `${fmt(data?.sellers.activeCount ?? 0)} active`,
    },
    {
      label: "Active SKUs",
      value: loading ? "—" : fmt(data?.listings.activeCount ?? 0),
      change: loading ? null : (data?.listings.dayOnDayActiveChangePct ?? 0),
      icon: Package,
      color: "emerald",
      sub: loading ? "" : `${fmt(data?.listings.totalCount ?? 0)} total`,
    },
    {
      label: "Pending Orders",
      value: loading ? "—" : fmt(data?.orders.pendingCount ?? 0),
      change: loading ? null : (data?.orders.dayOnDayChangePct ?? 0),
      icon: ShoppingCart,
      color: "amber",
      sub: loading ? "" : `${fmt(data?.orders.processedCount ?? 0)} processed`,
    },
    {
      label: "Revenue Today",
      value: loading ? "—" : formatCompactInr(data?.orders.todayRevenue ?? 0),
      change: loading ? null : (data?.orders.dayOnDayRevenueChangePct ?? 0),
      icon: TrendingUp,
      color: "rose",
      sub: loading
        ? ""
        : `Avg order: ${formatCompactInr(data?.orders.avgOrderValue ?? 0)}`,
    },
    {
      label: "Platform Users",
      value: loading ? "—" : fmt(data?.users.totalCount ?? 0),
      change: loading ? null : (data?.users.dayOnDayChangePct ?? 0),
      icon: Users,
      color: "violet",
      sub: loading ? "" : `${fmt(data?.users.activeCount ?? 0)} active`,
    },
    {
      label: "Connections",
      value: loading ? "—" : fmt(data?.connections.totalCount ?? 0),
      change: null,
      icon: Link2,
      color: "sky",
      sub: loading
        ? ""
        : `${fmt(data?.connections.connectedCount ?? 0)} connected · ${fmt(data?.connections.degradedCount ?? 0)} degraded`,
    },
  ];

  const colorClasses: Record<string, string> = {
    indigo:
      "bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400",
    emerald:
      "bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400",
    amber: "bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400",
    rose: "bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400",
    violet:
      "bg-violet-50 dark:bg-violet-950 text-violet-600 dark:text-violet-400",
    sky: "bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-400",
  };

  return (
    <DashboardLayout activePath="/">
      <PageHeader
        title="Dashboard"
        subtitle="Platform-wide overview across all sellers, orders, and revenue."
        breadcrumbs={[
          { label: "Home", active: false },
          { label: "Dashboard", active: true },
        ]}
        actions={
          <>
            <Button variant="outline" onClick={refetch} disabled={loading}>
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              onClick={() => showToast.info("Coming soon", "Add seller flow.")}
            >
              <Plus className="w-4 h-4" />
              Add Seller
            </Button>
          </>
        }
      />

      {error && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-rose-50 dark:bg-rose-950 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const isUp = (stat.change ?? 0) >= 0;
          return (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-lg ${colorClasses[stat.color]}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  {stat.change !== null && (
                    <span
                      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                        isUp
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      {isUp ? (
                        <ArrowUpRight className="w-3 h-3" />
                      ) : (
                        <ArrowDownRight className="w-3 h-3" />
                      )}
                      {fmtPct(stat.change)}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {stat.value}
                </p>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                  {stat.label}
                </p>
                {stat.sub && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                    {stat.sub}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Orders by Status */}
        <Card>
          <CardHeader>
            <CardTitle>Orders by Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              (data?.orders.byStatus ?? []).map((s) => (
                <div
                  key={s.status}
                  className="flex items-center justify-between"
                >
                  <StatusBadge status={s.status} showDot />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {s.count.toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Listings by Provider */}
        <Card>
          <CardHeader>
            <CardTitle>Listings by Marketplace</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              (data?.listings.byProvider ?? []).map((p) => (
                <div key={p.provider}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {p.provider}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {p.activeCount.toLocaleString()} /{" "}
                      {p.totalCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{
                        width:
                          p.totalCount > 0
                            ? `${Math.round((p.activeCount / p.totalCount) * 100)}%`
                            : "0%",
                      }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Subscription Plan Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Subscription Plans</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              <>
                {(data?.subscriptions.byPlan ?? []).map((p) => (
                  <div
                    key={p.plan}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {p.plan}
                    </span>
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {p.count.toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1">
                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                    <span>Trialing</span>
                    <span>{data?.subscriptions.trialing ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-rose-600 dark:text-rose-400">
                    <span>Past Due</span>
                    <span>{data?.subscriptions.pastDue ?? 0}</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue summary + Connections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              <>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Total Revenue
                  </span>
                  <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    {formatCompactInr(data?.orders.totalRevenue ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Today
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {formatCompactInr(data?.orders.todayRevenue ?? 0)}
                    </span>
                    <span
                      className={`text-xs font-medium ${
                        (data?.orders.dayOnDayRevenueChangePct ?? 0) >= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      {fmtPct(data?.orders.dayOnDayRevenueChangePct ?? 0)} vs
                      yesterday
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Last 7 Days
                  </span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {formatCompactInr(data?.orders.sevenDayRevenue ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Avg Order Value
                  </span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {formatCompactInr(data?.orders.avgOrderValue ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline pt-2 border-t border-slate-100 dark:border-slate-800">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Total Orders
                  </span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {(data?.orders.totalCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Processed (Shipped + Delivered)
                  </span>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                    {(data?.orders.processedCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Pending
                  </span>
                  <span className="font-semibold text-amber-700 dark:text-amber-400">
                    {(data?.orders.pendingCount ?? 0).toLocaleString()}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Marketplace Connections */}
        <Card>
          <CardHeader>
            <CardTitle>Marketplace Connections</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    {
                      label: "Connected",
                      value: data?.connections.connectedCount ?? 0,
                      cls: "text-emerald-700 dark:text-emerald-400",
                    },
                    {
                      label: "Degraded",
                      value: data?.connections.degradedCount ?? 0,
                      cls: "text-amber-700 dark:text-amber-400",
                    },
                    {
                      label: "Disconnected",
                      value: data?.connections.disconnectedCount ?? 0,
                      cls: "text-rose-700 dark:text-rose-400",
                    },
                  ].map((c) => (
                    <div
                      key={c.label}
                      className="text-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800"
                    >
                      <p className={`text-2xl font-bold ${c.cls}`}>{c.value}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {c.label}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="space-y-1 pt-2">
                  {(data?.connections.byProvider ?? []).map((p) => (
                    <div
                      key={p.provider}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-slate-600 dark:text-slate-400">
                        {p.provider}
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {p.count}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Sync Jobs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Recent Sync Jobs</span>
            {!loading && (data?.recentSync.failedLast24h ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400">
                <AlertTriangle className="w-3.5 h-3.5" />
                {data?.recentSync.failedLast24h} failed in last 24h
              </span>
            )}
            {!loading && (data?.recentSync.runningCount ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 dark:text-sky-400 ml-3">
                <Clock className="w-3.5 h-3.5" />
                {data?.recentSync.runningCount} running
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="px-4 py-6 text-sm text-slate-400">Loading…</p>
          ) : (data?.recentSync.recentJobs ?? []).length === 0 ? (
            <div className="px-4 py-6 flex items-center gap-2 text-sm text-slate-400">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              No recent sync jobs
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    {[
                      "Provider",
                      "Type",
                      "Status",
                      "Items",
                      "Started",
                      "Finished",
                      "Error",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {(data?.recentSync.recentJobs ?? []).map((job, i) => (
                    <tr
                      key={i}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
                        {job.provider}
                      </td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-400">
                        {job.syncType}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge
                          status={syncStatusMap[job.status] ?? job.status}
                          showDot
                        />
                      </td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-400">
                        {job.itemsProcessed.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {fmtDate(job.startedAt)}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {fmtDate(job.finishedAt)}
                      </td>
                      <td
                        className="px-4 py-2 text-xs text-rose-600 dark:text-rose-400 max-w-xs truncate"
                        title={job.errorMessage ?? ""}
                      >
                        {job.errorMessage ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
